import { DirectoryTenantPick, importDirectory } from './directory-import';
import { addVendor, ConsoleState } from './store';

interface PosBusiness {
  id: string;
  pos?: string;
}

interface PosTenant<B extends PosBusiness> {
  id: string;
  businesses: B[];
}

export interface PosVendor {
  pos: string;
  tenants: number;
  businesses: number;
}

const keyOf = (pos: string) => pos.trim().toLowerCase().replace(/\s+/g, '');

export const samePos = (left?: string, right?: string): boolean =>
  Boolean(left?.trim() && right?.trim()) && keyOf(left!) === keyOf(right!);

export const posVendors = <B extends PosBusiness>(entries: PosTenant<B>[]): PosVendor[] => {
  const found = new Map<string, { pos: string; tenants: Set<string>; businesses: number }>();
  for (const entry of entries)
    for (const business of entry.businesses) {
      if (!business.pos?.trim()) continue;
      const key = keyOf(business.pos);
      const held = found.get(key) ?? { pos: business.pos.trim(), tenants: new Set<string>(), businesses: 0 };
      held.tenants.add(entry.id);
      held.businesses += 1;
      found.set(key, held);
    }

  return [...found.values()]
    .map(({ pos, tenants, businesses }) => ({ pos, tenants: tenants.size, businesses }))
    .sort((a, b) => b.businesses - a.businesses || a.pos.localeCompare(b.pos));
};

export const shopsUsing = <B extends PosBusiness, T extends PosTenant<B>>(entries: T[], pos: string): T[] =>
  entries.flatMap((entry) => {
    const businesses = entry.businesses.filter((business) => samePos(business.pos, pos));
    return businesses.length ? [{ ...entry, businesses }] : [];
  });

export interface CasaSyncLine {
  pos: string;
  vendorId: string;
  created: boolean;
  tenants: number;
  newTenants: number;
  businesses: number;
  newBusinesses: number;
  left: string[];
}

const slugOf = (name: string) =>
  name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

const vendorFor = (state: ConsoleState, pos: string): { state: ConsoleState; id: string; created: boolean } => {
  const held = state.vendors.find((item) => !item.archived && samePos(item.pos, pos));
  if (held) return { state, id: held.id, created: false };
  const added = addVendor(state, pos, []);
  const id =
    added === state
      ? state.vendors.find((item) => (item.slug ?? item.id) === slugOf(pos))?.id
      : added.vendors.at(-1)?.id;
  if (!id) return { state, id: '', created: false };
  return {
    state: { ...added, vendors: added.vendors.map((item) => (item.id === id ? { ...item, pos } : item)) },
    id,
    created: added !== state,
  };
};

export const syncFromCasa = (
  state: ConsoleState,
  entries: DirectoryTenantPick[],
  now?: string,
): { state: ConsoleState; lines: CasaSyncLine[] } => {
  const tenants = entries.map((entry) => ({ ...entry, businesses: entry.businesses ?? [] }));
  const lines: CasaSyncLine[] = [];
  let next = state;
  for (const { pos } of posVendors(tenants)) {
    const vendor = vendorFor(next, pos);
    if (!vendor.id) continue;
    next = vendor.state;
    const shops = shopsUsing(tenants, pos);
    const connected = new Set(next.vendors.find((item) => item.id === vendor.id)?.tenantIds ?? []);
    const known = new Set((next.businesses ?? []).map((business) => business.id));
    const inCasa = new Set(shops.flatMap((shop) => shop.businesses.map((business) => business.id)));
    const left = (state.businesses ?? [])
      .filter((business) => connected.has(business.tenantId) && samePos(business.pos, pos) && !inCasa.has(business.id))
      .map((business) => business.name);
    next = importDirectory(next, shops, vendor.id, now, false);
    lines.push({
      pos,
      vendorId: vendor.id,
      created: vendor.created,
      tenants: shops.length,
      newTenants: shops.filter((shop) => !connected.has(shop.id)).length,
      businesses: shops.reduce((sum, shop) => sum + shop.businesses.length, 0),
      left,
      newBusinesses: shops.reduce((sum, shop) => sum + shop.businesses.filter((item) => !known.has(item.id)).length, 0),
    });
  }
  for (const vendor of casaVendors(next).filter((item) => !lines.some((line) => samePos(line.pos, item.pos)))) {
    const left = (state.businesses ?? [])
      .filter((business) => vendor.tenantIds.includes(business.tenantId) && samePos(business.pos, vendor.pos))
      .map((business) => business.name);
    lines.push({
      pos: vendor.pos!,
      vendorId: vendor.id,
      created: false,
      tenants: 0,
      newTenants: 0,
      businesses: 0,
      newBusinesses: 0,
      left,
    });
  }
  return { state: next, lines };
};

export const casaVendors = (state: ConsoleState) => state.vendors.filter((vendor) => !vendor.archived && vendor.pos);

export const casaBusinesses = (state: ConsoleState, vendorId: string, tenantId: string) => {
  const pos = state.vendors.find((vendor) => vendor.id === vendorId)?.pos;
  return (state.businesses ?? []).filter((item) => item.tenantId === tenantId && samePos(item.pos, pos));
};
